import { APIErrorResponse } from '../serviceHelpers/APIErrorResponse';
import JSONResponseHandler from '../serviceHelpers/JSONResponseHandler';
import Identity from './identity';
import { UserIdentity, isUserIdentity } from './userIdentity';
import { headers, logPrefix } from './constants';

import enforceRecaptcha, { RecaptchaMethod } from '../utils/enforceRecaptcha';
import { isJwtExpired, jwtHasExp } from '../utils/jwt';

/**
 * Logs a user in, optionally passing in a captchaToken.
 *
 * @param userName must be a valid email address
 * @param password
 * @param options optional login options. Specify `rememberMe: true` for a persistent session. Specify `cookie: true` to store user credentials in browser cookies. Mobile browsers will automatically save credentials in cookies.
 *
 *
 * If `Identity.login` is successful, the SDK will receive an auth & refresh token and save them to the browser. These tokens can be accessed at `Identity.userIdentity`.
 *
 * When logging in without specifying an `options` object or `{rememberMe: false}` as the options object, the SDK will save the user's tokens to the browser SessionStorage & will clear these tokens at the end of the browser session.
 *
 * If you pass in `{rememberMe: true}`, the SDK will save the user's tokens to the browser's LocalStorage & persist tokens between browser sessions.
 *
 * To check if a user is currently logged in, call `Identity.isLoggedIn()` [[isLoggedIn]]
 *
 *
 * @returns
 * A __Promise__ with the user information. When successful, `userIdentity` will be populated with the data fetched by this method.
 *
 * <details>
 * <summary>
 * On success (resolves)
 * </summary>
 *
 * ```json
 * {
 *   "uuid": "c7e9fed4-bedf-428b-a694-1bbd21721cf7",
 *   "accessToken": "c7e9fed4bedf428b.a6941bbd21721cf7...",
 * }
 * ```
 * </details>
 *
 * <details>
 * <summary>
 * On error (throws)
 * </summary>
 *
 * ```
 * See documentation for corresponding error codes:
 * https://redirector.arcpublishing.com/alc/docs/swagger/?url=./arc-products/arc-identity-v1.json#/Authentication/login
 * ```
 * </details>
 */

export default function login(
  userName: string,
  password: string,
  options: {
    rememberMe?: boolean;
    recaptchaToken?: string;
    cookie?: boolean;
  } = {}
): Promise<APIErrorResponse | UserIdentity> {
  Identity._isSession = !options.rememberMe;

  return enforceRecaptcha(RecaptchaMethod.SIGN_IN, options.recaptchaToken)
    .then(() =>
      fetch(
        `${Identity.apiOrigin}/identity/public/v1/auth/login${options.cookie ? '?cookie=true' : ''
        }`,
        {
          method: 'POST',
          headers,
          body: JSON.stringify({
            userName,
            credentials: password,
            grantType: 'password',
            rememberMe: options.rememberMe,
            recaptchaToken: options.recaptchaToken
          })
        }
      )
    )
    .then(JSONResponseHandler)
    .then(json => {
      if (isUserIdentity(json)) {
        Identity.userIdentity = json;
        window.postMessage({ type: 'ARC_LOGIN_SUCCESS', grantType: 'password' }, '*');
        return Identity.userIdentity;
      } else {
        throw json;
      }
    });
}

/**
 * Determines if a user is logged in or not. A user is logged in if any of the following are true:
 * - Identity.userIdentity.accessToken exists and is not expired
 * - Identity.userIdentity.refreshToken is not expired and a refresh (by calling Identity.extendSession) is successful.
 *
 * If both of the above are false, then a user is deemed as logged out.
 *
 * @returns _Promise_ which resolves to either true or false.
 *
 */

class HttpError extends Error {
  code: number;

  constructor(code: number, message: string) {
    super(message);
    this.name = 'HttpError';
    this.code = code;
  }
}

export async function isLoggedIn(): Promise<boolean> {
  if (
    !Identity.userIdentity ||
    !Identity.userIdentity.accessToken ||
    !Identity.userIdentity.refreshToken
  ) {
    return false;
  } else if (
    isJwtExpired(Identity.userIdentity.accessToken) ||
    !jwtHasExp(Identity.userIdentity.accessToken)
  ) {
    // attempt a refresh
    try {
      await Identity.extendSession();
      return true;
    } catch (e) {
      if (e instanceof HttpError) {
        console.warn(`${logPrefix} code:"${e.code}", message:"${e.message}"`);
      }

      return false;
    }
  } else {
    return true;
  }
}
